import Card from 'react-bootstrap/Card'
import Table from 'react-bootstrap/Table'
import Badge from 'react-bootstrap/Badge'

import { formatCurrency } from '../../utilities/helpers'

import './dynamicPricingPreviewResults.scss'

function MetricItem({ label, value }) {
  return (
    <div className="dynamic-pricing-preview-results__metric">
      <span className="dynamic-pricing-preview-results__metric-label">
        {label}
      </span>
      <span className="dynamic-pricing-preview-results__metric-value">
        {value}
      </span>
    </div>
  )
}

function formatMetricValue(key, value) {
  if (value == null) return '—'
  if (key === 'inventoryRemainingPct') {
    return `${(Number(value) * 100).toFixed(1)}%`
  }
  if (key === 'hoursUntilEvent') {
    return `${Number(value).toFixed(1)} hrs`
  }
  if (key === 'salesVelocityPerHour') {
    return `${Number(value).toFixed(2)}/hr`
  }
  return String(value)
}

const METRIC_LABELS = {
  inventoryRemainingPct: 'Inventory Remaining',
  hoursUntilEvent: 'Hours Until Event',
  salesVelocityPerHour: 'Sales Velocity',
}

export default function DynamicPricingPreviewResults({ data }) {
  if (!data) return null

  const metrics = data.metrics || {}
  const changes = data.changes || []
  const changing = changes.filter((change) => change.wouldChange)

  return (
    <Card
      body
      className="card--md card-with-border dynamic-pricing-preview-results mb-4"
      data-testid="dynamic-pricing-preview-results"
    >
      <div className="dynamic-pricing-preview-results__header">
        <div>
          <h2 className="h4 mb-1">Price Preview</h2>
          <p className="text-muted mb-0">
            Dry run only — no prices were saved. Review proposed changes before
            applying rules.
          </p>
        </div>
        <Badge bg="info">Preview</Badge>
      </div>

      <div className="dynamic-pricing-preview-results__metrics">
        {Object.entries(METRIC_LABELS).map(([key, label]) => (
          <MetricItem
            key={key}
            label={label}
            value={formatMetricValue(key, metrics[key])}
          />
        ))}
      </div>

      <p className="dynamic-pricing-preview-results__summary mb-3">
        {data.summary?.wouldChange ?? 0} of {data.summary?.total ?? 0} pricing
        level(s) would change.
      </p>

      {changing.length ? (
        <div className="table-responsive">
          <Table className="dynamic-pricing-preview-results__table">
            <thead>
              <tr>
                <th>Pricing Level</th>
                <th>Current Price</th>
                <th>Proposed Price</th>
                <th>Change</th>
                <th>Rules Applied</th>
              </tr>
            </thead>
            <tbody>
              {changing.map((change) => (
                <tr key={change.pricingObjectId}>
                  <td>{change.name}</td>
                  <td>{formatCurrency(change.currentPrice)}</td>
                  <td>{formatCurrency(change.adjustedPrice)}</td>
                  <td
                    className={
                      change.delta >= 0
                        ? 'dynamic-pricing-preview-results__delta--up'
                        : 'dynamic-pricing-preview-results__delta--down'
                    }
                  >
                    {change.delta >= 0 ? '+' : ''}
                    {formatCurrency(change.delta)}
                  </td>
                  <td>
                    {(change.appliedRules || [])
                      .map((rule) => rule.name || `Rule #${rule.id}`)
                      .join(', ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : (
        <p className="mb-0 text-muted">
          No pricing levels would change with the current rules and live
          metrics.
        </p>
      )}
    </Card>
  )
}
